// >>> **HER BUTTON RULING (2026-08-14) — WHERE A BROADCAST BUTTON CAN POINT, OFFERED AS A PICKER.**
//
// Her clause: *"The composer's button editor offers a simple picker (Home, the rooms, the funnels,
// or paste any site path), never requiring her to hand-type full URLs unless she wants to."*
//
// **EVERY ENTRY IS DERIVED, AND NOT ONE PATH IS TYPED HERE (R11/R31).** `PAGE_PATHS` is the house's
// one both-ways route map and `funnelUrl` is the one place a funnel's query string is spelled — the
// same declaration her ad creative is quoted from. A hand-typed list would agree on the day it was
// written and rot silently: a renamed route leaves a button in a marketing email pointing at a page
// that no longer exists, and the only instrument for THAT is a member telling her the link is dead.
//
// **THE SHAPE IS A PATH AND NEVER AN ABSOLUTE URL**, because `emailAbsoluteUrl` resolves it against
// `SITE_URL` at composition time — so the site's origin is stated once in the house and a picker
// entry cannot carry a stale domain into a mail client.
//
// **WHAT IS DELIBERATELY NOT OFFERED, NAMED RATHER THAN SILENTLY DROPPED (R17/R160):** the owner
// dashboard (a member tapping it meets a 403, so a marketing button that reaches it is a dead end),
// and the legal pages, which are in the footer of every mail she sends already. Neither is refused —
// the free-text field takes any path she wants, which is her own *"or paste any site path"*.
import { PAGE_PATHS, PAGE_TITLES, type Page } from "./pages";
import { ROOM_FOR_PAGE } from "../rooms.js";
import { FUNNEL_KEYS, funnelCopy, funnelUrl } from "../funnels.js";
import { TALK_PATH } from "../talkDoor.js";

export type BroadcastDestination = { label: string; path: string; group: string };

/** The one page every member has (`PAGE_PATHS`' own first entry, read rather than spelled). */
const HOME: BroadcastDestination[] = [{ label: "Home page", path: PAGE_PATHS.home, group: "The house" }];

/**
 * **THE ROOMS, OFF `ROOM_FOR_PAGE` — WHICH IS THE ROSTER THAT ANSWERS *IS THIS PAGE A ROOM*.**
 *
 * Two of its keys resolve to one room (`magic-room` and `spell-book` are both the Magic Room) and
 * one of them is not a `Page` at all, so the list is keyed on the RESOLVED PATH and de-duplicated by
 * it: one entry per destination, which is what a picker is a picker of. A key this map holds that
 * `PAGE_PATHS` cannot route is dropped **because it cannot be linked**, and that is a property of
 * the two rosters rather than a judgement made here.
 */
const ROOMS: BroadcastDestination[] = Object.keys(ROOM_FOR_PAGE)
  .filter((page): page is Page => Object.prototype.hasOwnProperty.call(PAGE_PATHS, page))
  .map((page) => ({ label: PAGE_TITLES[page] || page, path: PAGE_PATHS[page], group: "The rooms" }))
  .filter((d, i, all) => all.findIndex((x) => x.path === d.path) === i);

/**
 * **THE THREE FUNNEL DOORS, COMPOSED BY `funnelUrl` WITH NO ORIGIN.** Passing `""` is what makes it
 * answer a bare path; the alternative is re-spelling `?f=` here, which is the one string in this
 * house that is also sitting inside live ad creative (F1's ruling).
 */
const FUNNELS: BroadcastDestination[] = FUNNEL_KEYS.map((key: string) => ({
  label: `Funnel · ${funnelCopy(key).title}`,
  path: funnelUrl(key, ""),
  group: "The funnels",
}));

/**
 * >>> HER ITEM 6: ALL FOUR FUNNELS ARE SELECTABLE, AND `/talk` IS THE FOURTH. <<<
 *
 * **IT IS NOT ADDED TO `FUNNEL_KEYS`, AND THAT REFUSAL IS THE WHOLE CARE.** `src/talkDoor.js`
 * rules on this in as many words: a key on that roster is a door that CAPTURES A LEAD AND DELIVERS
 * A PAID READING BY EMAIL, and her ITEM 2 for `/talk` is SIGNUP FIRST -- so a fourth key would buy
 * a generation her own flow does not want and land a stranger on a reading rather than an account.
 *
 * A PICKER DESTINATION IS A DIFFERENT FACT (R18): it answers *where does this button go*, and that
 * `/talk` is not a lead funnel says nothing about whether she may point a broadcast at it. So the
 * door joins the list she picks from and the roster that decides what a funnel IS stays untouched.
 *
 * The path is `TALK_PATH` rather than the string, because `talkDoor.js` is the one place `/talk` is
 * spelled and a second spelling here is a button that 404s the day that page moves (R28/R29).
 */
const TALK_DOOR: BroadcastDestination = {
  label: "Funnel · Ask Lala",
  path: TALK_PATH,
  group: "The funnels",
};

export const BROADCAST_DESTINATIONS: BroadcastDestination[] = [...HOME, ...ROOMS, ...FUNNELS, TALK_DOOR];

/** The groups in the order they are drawn, derived so a fourth group joins by existing. */
export const DESTINATION_GROUPS: string[] = BROADCAST_DESTINATIONS
  .map((d) => d.group)
  .filter((g, i, all) => all.indexOf(g) === i);

/**
 * **WHICH PICKER ENTRY A DESTINATION IS, OR `""` FOR ONE SHE TYPED HERSELF.** The picker is a
 * convenience over a free-text field and never a replacement for it, so *not on this list* is an
 * ordinary answer rather than a refusal — it is what her *"unless she wants to"* means.
 */
export const destinationForPath = (href: string): BroadcastDestination | null =>
  BROADCAST_DESTINATIONS.find((d) => d.path === String(href || "").trim()) || null;
